import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationNotEnabled from "../shared/IntegrationNotEnabled";
import MindbodyDataMapping from "./MindbodyDataMapping";
import LaunchWizardButton from "@/components/LaunchWizardButton";

const MB_COLOR = "#00A651";

// ─── Exported nav helpers ──────────────────────────────────────────────────────

export function BackToIntegrationLandingMindbody() {
  const navigate = useNavigate();
  return (
    <button type="button" className="flex cursor-pointer items-center gap-2 text-slate-600"
      onClick={() => navigate("/integrations")}>
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
      </svg>
      Back to Integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface MindbodyConfig {
  id: number;
  siteId: number;
  displayName: string;
  businessName: string;
  mindbodySiteId: number;
  enabled: boolean;
  createdAt: string;
}

type TabId = "overview" | "accounts" | "ai-tools" | "data-mapping" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Class schedule & availability",
    icon: "📅",
    description: "Answer 'when is yoga?', 'is spin class full?', 'what's on today?' in real time"
  },
  {
    title: "Self-service booking",
    icon: "📋",
    description: "Members book, waitlist, or cancel classes directly in chat — no login needed"
  },
  {
    title: "Member account",
    icon: "👤",
    description: "Membership status, visit history, account balance, and upcoming bookings at a glance"
  },
  {
    title: "Staff & location info",
    icon: "🧑‍🏫",
    description: "Instructor bios, class descriptions, studio hours, and location details on demand"
  },
  {
    title: "Pricing & services",
    icon: "💰",
    description: "Membership options, drop-in rates, and package pricing always up to date"
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { name: "mindbody_get_classes", label: "Get classes", group: "Classes", description: "Retrieve all classes available at the studio for a given date or date range." },
  { name: "mindbody_search_classes", label: "Search classes", group: "Classes", description: "Search classes by name, instructor, or type. Returns matching classes with schedule and availability." },
  { name: "mindbody_get_class_schedule", label: "Get class schedule", group: "Classes", description: "Full weekly or monthly class schedule for the studio with instructor and capacity info." },
  { name: "mindbody_get_class_description", label: "Get class description", group: "Classes", description: "Detailed description for a class including fitness level, what to bring, and duration." },
  { name: "mindbody_check_class_availability", label: "Check class availability", group: "Classes", description: "Real-time availability for a specific class — spots remaining, waitlist status." },
  { name: "mindbody_book_class", label: "Book class", group: "Bookings", description: "Book a member into a class or add them to the waitlist. Returns confirmation details." },
  { name: "mindbody_cancel_booking", label: "Cancel booking", group: "Bookings", description: "Cancel a class booking and apply late-cancel policy if applicable." },
  { name: "mindbody_get_client_bookings", label: "Get client bookings", group: "Bookings", description: "All upcoming and past bookings for a member — class name, date, instructor, and status." },
  { name: "mindbody_get_booking_details", label: "Get booking details", group: "Bookings", description: "Full details for a specific booking including check-in status and any notes." },
  { name: "mindbody_get_client", label: "Get client profile", group: "Members", description: "Member profile: name, email, phone, membership status, and account details." },
  { name: "mindbody_search_clients", label: "Search clients", group: "Members", description: "Search members by name, email, or phone number. Returns matching profiles." },
  { name: "mindbody_get_client_memberships", label: "Get client memberships", group: "Members", description: "Active memberships for a member — plan name, status, renewal date, and class credits." },
  { name: "mindbody_get_client_visits", label: "Get client visits", group: "Members", description: "Visit history for a member — classes attended, dates, and instructors." },
  { name: "mindbody_get_client_account_balance", label: "Get account balance", group: "Members", description: "Current account balance, credits, and outstanding balance for a member." },
  { name: "mindbody_get_staff", label: "Get staff", group: "Staff", description: "All staff members with names, roles, and bio information." },
  { name: "mindbody_get_staff_schedule", label: "Get staff schedule", group: "Staff", description: "Schedule for a specific instructor — which classes they are teaching and when." },
  { name: "mindbody_get_locations", label: "Get locations", group: "Locations & Info", description: "All studio locations with addresses, phone numbers, and hours." },
  { name: "mindbody_get_site_info", label: "Get site info", group: "Locations & Info", description: "General studio information — name, description, contact details, and amenities." },
  { name: "mindbody_get_programs", label: "Get programs", group: "Locations & Info", description: "All programs and class types offered at the studio — yoga, pilates, cycling, etc." },
  { name: "mindbody_get_services", label: "Get services", group: "Locations & Info", description: "All services available including class packs, appointments, and workshops." },
  { name: "mindbody_get_pricing", label: "Get pricing", group: "Locations & Info", description: "Current pricing for memberships, drop-ins, class packs, and special offers." }
];

// ─── Shared UI Components ─────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl" aria-expanded={open}>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">{CAPABILITIES.length} feature areas</span>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect Mindbody (API key + Site ID) and the AI gains access to classes, members, bookings, and studio info in real time.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-4 flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{cap.icon}</span>
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-0.5">{cap.title}</div>
                  <div className="text-xs text-slate-500">{cap.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HowItWorksCard() {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
      <div className="font-semibold text-slate-800 text-base mb-1">How Mindbody works in Velaro</div>
      <p className="text-xs text-slate-500 mb-4">Connect once — every class, member, and booking becomes AI-searchable instantly.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ backgroundColor: MB_COLOR }}>1</span>
            <span className="font-semibold text-slate-700 text-sm">Connect Mindbody</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">In Mindbody → Account → Integrations → API → copy your API Key → find your Site ID in Account → Business → Site Details.</p>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">API Key + Site ID</span>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">22 AI tools activate</span>
          </div>
          <p className="text-xs text-green-700 mb-3">Every class, member profile, and booking is instantly searchable — no extra setup.</p>
          <div className="grid grid-cols-2 gap-1">
            {["Class schedule", "Booking", "Cancellation", "Member lookup", "Memberships", "Pricing", "Staff info", "Locations", "Visit history", "Account balance"].map((t) => (
              <div key={t} className="flex items-center gap-1 text-[10px] text-green-700">
                <svg viewBox="0 0 8 8" width="8" height="8" fill="none" className="flex-shrink-0"><path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" /></svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">Scale 24/7 member support</span>
          </div>
          <p className="text-xs text-blue-700 mb-3">Let members self-serve bookings and get answers instantly — without calling the front desk.</p>
          <div className="space-y-2">
            {[
              ["Class booking bot", "Members book or cancel classes directly in chat 24/7"],
              ["Schedule assistant", "Answers 'what's on today?' and 'is there space?' instantly"],
              ["Member account bot", "Balance, memberships, and visit history on demand"]
            ].map(([title, desc]) => (
              <div key={title} className="bg-white rounded-lg p-2 border border-blue-100">
                <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">{title}</div>
                <div className="text-[10px] text-blue-600 mt-0.5">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ hasConnection, onConnect, onOpenWizard }: { hasConnection: boolean; onConnect(): void; onOpenWizard(): void }) {
  return (
    <div>
      <HowItWorksCard />
      {!hasConnection ? (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 mb-1">Connect your Mindbody account</div>
            <p className="text-sm text-slate-500">Enter your API key and Site ID and your classes, members, bookings, and studio data become instantly searchable by the AI during any conversation.</p>
          </div>
          <button type="button" onClick={onConnect} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: MB_COLOR }}>
            Connect Now →
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 mb-1">Launch Starter Workflow Wizard</div>
            <p className="text-sm text-slate-500">Build a Class Booking Bot, Schedule Assistant, or Member Account Bot in minutes using a pre-built Mindbody template.</p>
          </div>
          <button type="button" onClick={onOpenWizard} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: MB_COLOR }}>
            Launch Wizard →
          </button>
        </div>
      )}
    </div>
  );
}

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
            AI Tools
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
              {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">All tools activate automatically on connect — no configuration required.</p>
        </div>
        {hasConnection && (
          <LaunchWizardButton onClick={onOpenWizard} />
        )}
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div key={tool.name} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: MB_COLOR }} />
                    <div>
                      <div className="text-xs font-semibold text-slate-700">{tool.label}</div>
                      <div className="text-[10px] font-mono text-slate-400 mb-1">{tool.name}</div>
                      <div className="text-xs text-slate-500">{tool.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountsTab({ configs, onRefresh, onAdd }: { configs: MindbodyConfig[]; onRefresh(): void; onAdd(): void }) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  async function handleDelete(id: number) {
    if (!confirm("Remove this Mindbody connection?")) return;
    setDeleting(id);
    try { await api.admin.delete(`Mindbody/Configs/${id}`); onRefresh(); } catch { /* graceful */ } finally { setDeleting(null); }
  }
  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold text-slate-800 text-lg">Connected Accounts</div>
        <button type="button" onClick={onAdd} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: MB_COLOR }}>
          + Add Account
        </button>
      </div>
      {configs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <div className="text-4xl mb-3">📅</div>
          <div className="font-medium text-slate-600 mb-1">No Mindbody account connected</div>
          <p className="text-sm">Connect your Mindbody API key to enable AI-powered class booking and member support.</p>
          <button type="button" onClick={onAdd} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: MB_COLOR }}>
            Connect Mindbody →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: MB_COLOR }}>M</div>
                <div>
                  <div className="font-medium text-slate-800">{c.displayName}</div>
                  {c.businessName && <div className="text-xs text-slate-500">{c.businessName}</div>}
                  <div className="text-xs text-slate-400 font-mono">Site ID: {c.mindbodySiteId}</div>
                  <div className="text-xs text-slate-400">{new Date(c.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                  {c.enabled ? "Active" : "Disabled"}
                </span>
                <button type="button" onClick={() => handleDelete(c.id)} disabled={deleting === c.id} className="text-xs text-red-500 hover:text-red-700 transition-colors">
                  {deleting === c.id ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Mindbody() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<MindbodyConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const { teamId } = useSelectedTeam();

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Mindbody/Configs");
      if (resp.status === 402) { setEnabled(false); return; }
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  if (!enabled) {
    return <IntegrationNotEnabled name="Mindbody Integration" description="Mindbody integration requires an upgrade. Contact Velaro support to enable it for your account." />;
  }

  const hasActiveConfig = configs.some((c) => c.enabled);

  const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "accounts", label: "Accounts" },
    { id: "ai-tools", label: "AI Tools" },
    { id: "data-mapping", label: "Data Mapping" },
    { id: "logs", label: "Activity Log" }
  ];

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard integration="mindbody" onClose={() => setShowWizard(false)} />
      )}
      <CapabilitiesPanel />
      <div className="flex border-b border-slate-200 mb-4">
        {TABS.map((tab) => (
          <button key={tab.id} type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === tab.id ? "border-b-2 font-medium" : "text-slate-500"}`}
            style={activeTab === tab.id ? { borderColor: MB_COLOR, color: MB_COLOR } : {}}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
            {tab.id === "accounts" && configs.length > 0 && (
              <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>
            )}
          </button>
        ))}
      </div>

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Mindbody integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</a>
              <a href="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition">Get Embed Code →</a>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> Use <strong>Page Widgets</strong> from{" "}
              <a href="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</a> to add a
              lightweight class schedule widget on your studio website — no live agent required.
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab hasConnection={hasActiveConfig}
              onConnect={() => navigate("/integrations/mindbody/create")}
              onOpenWizard={() => setShowWizard(true)} />
          )}
          {activeTab === "accounts" && (
            <AccountsTab configs={configs} onRefresh={fetchConfigs} onAdd={() => navigate("/integrations/mindbody/create")} />
          )}
          {activeTab === "ai-tools" && <AiToolsTab hasConnection={hasActiveConfig} onOpenWizard={() => setShowWizard(true)} />}
          {activeTab === "data-mapping" && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="font-semibold text-slate-800 text-lg mb-1">Data Mapping — Mindbody</div>
              <MindbodyDataMapping
                teamId={teamId ?? 0}
                selectedConfigId={selectedConfigId}
              />
            </div>
          )}
          {activeTab === "logs" && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <IntegrationActivityLog integrationSlug="Mindbody" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
